import { useEffect } from 'react'
import { Link, redirect, useLoaderData } from 'react-router'
import { useInView } from 'react-intersection-observer'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'
import { Panel } from '@/components/subscription/panel'
import { formatDate } from '@/lib/shipment'
import { yen } from '@/lib/price'
import { DeliverySummary, toDeliverySummary } from '@/lib/order'
import { isAppWebView } from '@/lib/app'
import { fetchSubscriptionOrderedPlacements, useSubscriptionOrderedPlacements } from '@/hooks/use-subscription-ordered-placements'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import * as styles from './styles.css'

export const loader = async () => {
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  if (!subscription) {
    return redirect('/subscription/branch/mypage')
  }

  const placements = await fetchSubscriptionOrderedPlacements(subscription.id, 1)

  return [subscriptions, placements] as const
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'green' })
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  return (
    <div className="flex flex-col gap-1 w-full">
      <span className="text-[12px] text-[#666666] leading-relaxed tracking-wide">{label}</span>
      <div className="text-[14px] text-[#212121] leading-relaxed tracking-wide">{children}</div>
    </div>
  )
}

const DeliveryCard: React.FC<{ delivery: DeliverySummary; showInvoice: boolean }> = ({ delivery, showInvoice }) => {
  return (
    <Panel>
      {delivery.description && (
        <>
          <div className={styles.flowerInfo}>
            <p className={styles.description}>{delivery.description}</p>
          </div>
          <div className={styles.divider} />
        </>
      )}
      <div className="flex flex-col gap-3 w-full">
        {delivery.deliverAt && <Field label="お届け日">{formatDate(delivery.deliverAt * 1000, 'yyyy年M月d日（E）')}</Field>}
        {delivery.address && (
          <Field label="お届け先">
            <p>
              {delivery.address.postalCode && `〒${delivery.address.postalCode} `}
              {delivery.address.address}
            </p>
            {delivery.address.name && <p>{delivery.address.name}</p>}
          </Field>
        )}
        <Field label="合計金額">{yen(delivery.total)}（税込）</Field>
      </div>
      {showInvoice && delivery.invoiceUrl && (
        <div>
          <a
            href={delivery.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-black text-white text-[13px] font-bold rounded-full"
          >
            領収書を表示する
          </a>
        </div>
      )}
    </Panel>
  )
}

export const SubscriptionBranchMypageOrders: React.FC = () => {
  const [
    {
      data: { data: subscriptions },
    },
    {
      data: { data: initialPlacements, included: initialIncluded },
    },
  ] = useLoaderData<typeof loader>()

  const subscription = subscriptions[0]
  const { placements, included, hasNextPage, loadMore } = useSubscriptionOrderedPlacements({
    subscriptionId: subscription?.id,
    initialPlacements,
    initialIncluded,
  })

  const deliveries = placements
    .map((placement) => ({ id: placement.id, summary: toDeliverySummary(placement, included) }))
    .filter((delivery): delivery is { id: string; summary: DeliverySummary } => delivery.summary !== null)

  // アプリのWebViewでは領収書PDFを開けないので出さない
  const showInvoice = !isAppWebView()

  const { ref, inView } = useInView({ rootMargin: '300px' })

  useEffect(() => {
    if (inView) {
      loadMore()
    }
  }, [inView, loadMore])

  return (
    <div className="flex flex-col gap-6 items-start w-full">
      <BranchPageHeading main="お届け履歴" />
      {deliveries.length === 0 ? (
        <Panel>
          <p className="text-[14px] text-black leading-relaxed tracking-wide">お届け履歴はありません</p>
        </Panel>
      ) : (
        deliveries.map((delivery) => <DeliveryCard key={delivery.id} delivery={delivery.summary} showInvoice={showInvoice} />)
      )}
      {hasNextPage && (
        <div className="flex justify-center w-full min-h-px" ref={ref}></div>
      )}
    </div>
  )
}
